<script setup lang="ts">
import { safeParse as vSafeParse } from 'valibot';
import { normalizeStringSearchParam } from '#shared/url';
import type { AgentOnList } from '#shared/agent/types';
import { ErrorSchema, type IadAppRoute } from '~/application';

definePageMeta({
  pageTemplate: 'search_page',
  pageType: 'collaborator',
});

const route = useRoute();
const locationSlug = computed(() => normalizeStringSearchParam(route.params.agentSector));
const query = toRef(route, `query`);
const { data: agentsBySector, suspense, error: agentBySectorError } = useSearchSectorAgents(locationSlug, query);

const page = useQueryParamNumber('page', 1);

await suspense();

const { t, locale } = useI18n();
const localePath = useLocalePath();

const errorResult = vSafeParse(ErrorSchema, agentBySectorError.value);

const searchAgentRoute: IadAppRoute<'find-real-estate-agent'> = {
  name: `find-real-estate-agent`,
  params: {},
};
if (errorResult.success && errorResult.output.statusMessage === `DB_AGENT_LIST_SECTOR_NOT_FOUND`) {
  const path = localePath(searchAgentRoute, locale.value);
  if (path) await navigateTo({ path }, { replace: true, redirectCode: 301 });
}
const hasResults = computed(() => (agentsBySector.value?.totalItems ?? 0) > 0);
const layerName = computed(() => agentsBySector.value?.location?.layerName ?? `adm1`);
const isUpperAdministrativeGroup = computed(() => layerName.value === 'adm1' || layerName.value === 'adm2');

const headTitle = computed(() => {
  let translationKey = 'agent.search_result.meta_title.';
  translationKey += isUpperAdministrativeGroup.value ? 'large_administrative_level' : 'small_administrative_level';
  return t(translationKey, {
    location: agentsBySector.value?.location?.displayNameWithCode,
  });
});
const metaDescription = computed(() => {
  return t(`agent.search_result.meta_description.${agentsBySector.value?.location.layerName}`, {
    displayName: agentsBySector.value?.location?.displayName,
    location: agentsBySector.value?.location?.displayNameWithCode,
  });
});

const config = useRuntimeConfig();

const canonicalLink = computed(() => {
  const routeParams = { agentSector: locationSlug.value as string };
  const pathParams: IadAppRoute<'find-real-estate-agent-agentSector'> = {
    name: 'find-real-estate-agent-agentSector',
    params: routeParams,
    query: {
      page: page.value > 1 ? page.value : undefined,
    },
  };
  const href = localePath(pathParams, locale.value);
  return new URL(href, config.public.i18n.baseUrl).href;
});

useHead({
  title: headTitle.value,
  link: [{
    id: 'i18n-can',
    rel: 'canonical',
    href: canonicalLink.value,
  }],
  meta: [
    { property: `og:title`, content: headTitle.value },
    { name: `description`, content: metaDescription.value },
    { property: `og:description`, content: metaDescription.value },
  ],
});

const title = computed(() => {
  const isExtendedSearch = agentsBySector.value?.isExtendedSearch;
  let translationKey = `agent.search_result.result_count`;
  if (isUpperAdministrativeGroup.value) translationKey += `_${layerName.value}`;
  else if (isExtendedSearch) translationKey += `_extended`;
  return t(translationKey, {
    count: agentsBySector.value?.totalItems,
    location: agentsBySector.value?.location?.displayNameWithCode,
  });
});

const agentList = computed(() => agentsBySector.value?.items ?? []);

const contactedAgent = ref<AgentOnList>();
watch(contactedAgent, function onContact(agentToContact) {
  if (!agentToContact) return;
  trackEvent({
    event: 'agent_interaction',
    event_name: 'click_to_email',
    agent_id: agentToContact.localId,
  });
  trackEvent({
    event: 'generate_lead',
    lead_category: 'buyer',
    agent_id: agentToContact.localId,
  });
});
const displayContactForm = computed({
  get() {
    return !!contactedAgent.value;
  },
  set(value: boolean) {
    if (!value) contactedAgent.value = undefined;
  },
});
</script>

<template>
  <div class="contents">
    <FindAgentForm />
    <IadWrapper v-if="hasResults">
      <IadTitle
        class="py-l"
        variant="large"
        :text="title"
      />
      <ol
        v-if="agentsBySector"
        class="grid grid-cols-[repeat(auto-fill,_minmax(320px,_1fr))] gap-2xl gap-2xl px-2xl xl:px-0"
      >
        <AgentCard
          v-for="agent of agentList"
          :key="agent.localId"
          :agent
          @contact="contactedAgent = agent"
        />
      </ol>
      <IadPagination
        v-if="agentsBySector?.totalItems"
        :page
        title-translation-key="agent.search_result.pagination"
        :total-items="agentsBySector?.totalItems"
        :items-per-page="agentsBySector?.itemsPerPage"
      />
      <section
        v-if="agentsBySector?.properties?.length"
        class="py-2xl"
      >
        <IadTitle
          variant="large"
          :text="t(`agent.search_result.latest_properties`, { place: agentsBySector.location.displayName })"
          class="pb-m"
          tag="div"
        />
        <IadScrollable variant="dense">
          <PropertyCardPromo
            v-for="property of agentsBySector?.properties"
            :key="property.propertyListingRef"
            :property
          />
        </IadScrollable>
      </section>
      <IadBreadcrumbs
        v-if="agentsBySector?.breadcrumbs"
        :items="agentsBySector.breadcrumbs"
        class="py-2xl"
      />
      <ClientOnly>
        <AgentDialogContact
          v-model="displayContactForm"
          :agent="contactedAgent"
        />
      </ClientOnly>
    </IadWrapper>
    <IadPlaceholderNotFound
      v-else
      :title="t(`agent.search_result.not_found.title`)"
      :description="t(`agent.search_result.not_found.description`, { city: agentsBySector?.location?.displayName })"
    />
    <PropertySerpStickyCta />
  </div>
</template>
